// The playoff bracket. Pure. BACKLOG E9a.
//
// This is the sibling of `./season.ts` and it stops at the same line: it takes
// standings rows and the playoff rules and answers who plays whom, in which
// period, under which bracket slot. It writes nothing, reads no clock, and
// knows about no sport.
//
// WHY IT LIVES HERE NOW. `packages/sim/src/playoffs.ts` has carried
// `bracketShape`, `bracketOrder` and `roundName` since 2026-09-11, and
// [screens/SCHEDULE.md](../../../../docs/design/screens/SCHEDULE.md) line 46
// said they "move to packages/domain when the API is built (they only need
// standings rows and the playoff settings)". The API is being built, so this
// is that move: the three functions are defined here and the simulator imports
// them, which is the only way the bracket a real league plays and the bracket
// the mockups draw can be the same bracket.
//
// WHAT A ROUND IS RESOLVED FROM. Round one is resolved from the seeds. Every
// later round is resolved from `results`, a map of bracket slot to the team
// that came out of it — which the caller builds from the final matchups it has
// stored. A side with no answer yet is `null` rather than a guess, so a caller
// can plan the whole bracket in week 14 for a projection and plan it again in
// week 16 for real without two code paths.
//
// A BYE IS NOT A GAME. The top seeds rest while the field is smaller than the
// bracket, and a resting seed is reported as a pairing with `bye: true` and no
// opponent. It exists in the plan because the next round needs to know who is
// waiting there; it is not a matchup and must not be written as one — which is
// the same rule `./season.ts` follows for a regular-season bye.

import type { PlayoffSettings, TiebreakerSettings } from '../rules/schemas';

export interface BracketShape {
  /** The smallest power of two that fits the field. */
  size: number;
  rounds: number;
  /** Top seeds that rest in round one. */
  byes: number;
}

export function bracketShape(teamCount: number): BracketShape {
  if (teamCount <= 1) return { size: 1, rounds: 0, byes: 0 };
  let size = 2;
  while (size < teamCount) size *= 2;
  return { size, rounds: Math.log2(size), byes: size - teamCount };
}

/**
 * "Quarterfinals", "Semifinals", "Championship", by distance from the final;
 * a consolation bracket's last round is its "Final", the title being the
 * winners' bracket's alone.
 */
export function roundName(index: number, rounds: number, consolation = false): string {
  const fromEnd = rounds - index;
  if (fromEnd === 1) return consolation ? 'Final' : 'Championship';
  if (fromEnd === 2) return 'Semifinals';
  if (fromEnd === 3) return 'Quarterfinals';
  return `Round ${index + 1}`;
}

/** Seeds in bracket order for a power-of-two field: [1, 8, 4, 5, 2, 7, 3, 6] for eight. */
export function bracketOrder(size: number): number[] {
  let order = [1];
  while (order.length < size) {
    const n = order.length * 2;
    const next: number[] = [];
    for (const s of order) next.push(s, n + 1 - s);
    order = next;
  }
  return order;
}

/**
 * The stored `bracket_slot` for a game.
 *
 * Named by distance from the final rather than by round number, because that
 * is what `migrations/0001` documents the column as holding ('qf1', 'sf2',
 * 'final') and what a person reading a matchup row expects. The second leg of
 * a two-period championship is `final2`: the same pairing, a different period,
 * and a distinct row because a matchup is scored per period.
 */
export function bracketSlot(round: number, rounds: number, game: number, leg = 0): string {
  const fromEnd = rounds - round;
  const base =
    fromEnd === 1
      ? 'final'
      : fromEnd === 2
        ? `sf${game + 1}`
        : fromEnd === 3
          ? `qf${game + 1}`
          : `r${round + 1}g${game + 1}`;
  return leg === 0 ? base : `${base}${leg + 1}`;
}

/** A team in the standings, reduced to what seeding needs to know about it. */
export interface StandingLine {
  teamId: string;
  /** 1-based, as `standings.rank` stores it. */
  rank: number;
  /** `teams.division_id`. Only read when `seeding` is `division-winners-first`. */
  divisionId?: string | null;
}

export interface SeedRow {
  /** 1-based. Seed 1 is the top seed. */
  seed: number;
  teamId: string;
}

/**
 * The playoff field, in seed order.
 *
 * `seeding: 'standings'` is the table, cut to `teamCount`. `'division-winners-first'`
 * puts the best-ranked team from each division above everyone else, in their own
 * rank order, and fills the rest of the field from the remaining teams by rank —
 * which is the convention every major league uses and what ESPN and Sleeper both
 * call "division winners get the top seeds". A league with no divisions gets the
 * table, because there are no winners to promote; the settings validator already
 * warns about that combination rather than refusing it.
 */
export function seedPlayoffField(
  rows: readonly StandingLine[],
  playoffs: Pick<PlayoffSettings, 'teamCount' | 'seeding'>,
): SeedRow[] {
  const table = [...rows].sort((a, b) => a.rank - b.rank || a.teamId.localeCompare(b.teamId));
  const wanted = Math.min(playoffs.teamCount, table.length);
  if (wanted <= 0) return [];

  let ordered = table;
  if (playoffs.seeding === 'division-winners-first') {
    const winners: StandingLine[] = [];
    const seen = new Set<string>();
    for (const row of table) {
      const division = row.divisionId ?? null;
      if (division === null || seen.has(division)) continue;
      seen.add(division);
      winners.push(row);
    }
    if (winners.length > 0) {
      const promoted = new Set(winners.map((w) => w.teamId));
      ordered = [...winners, ...table.filter((r) => !promoted.has(r.teamId))];
    }
  }

  return ordered.slice(0, wanted).map((row, i) => ({ seed: i + 1, teamId: row.teamId }));
}

/**
 * The consolation field (PLAN-2026-10-26-consolation-brackets §2): the teams
 * that miss the playoffs, as many as a bracket no deeper than the winners'
 * holds. A consolation bracket takes the best of them, best first, as ESPN
 * and Yahoo seat the places below the playoff field; a toilet bowl the worst,
 * the last team in the table its first seed, since a bye there moves a team
 * toward last place (Sleeper's). Empty with `none`, without a playoff
 * bracket, and with fewer than two to seat.
 */
export function consolationField(
  rows: readonly StandingLine[],
  playoffs: Pick<PlayoffSettings, 'teamCount' | 'seeding' | 'consolation'>,
): SeedRow[] {
  if (playoffs.consolation === 'none') return [];
  const field = seedPlayoffField(rows, playoffs);
  if (field.length < 2) return [];
  const inField = new Set(field.map((f) => f.teamId));
  const rest = [...rows]
    .sort((a, b) => a.rank - b.rank || a.teamId.localeCompare(b.teamId))
    .filter((r) => !inField.has(r.teamId));
  const seated = Math.min(rest.length, bracketShape(field.length).size);
  if (seated < 2) return [];
  const chosen =
    playoffs.consolation === 'toilet-bowl' ? rest.slice(-seated).reverse() : rest.slice(0, seated);
  return chosen.map((r, i) => ({ seed: i + 1, teamId: r.teamId }));
}

/**
 * Each standings row with the seed it would hold if the regular season ended
 * now (`StandingsRow.seed`): `seedPlayoffField` over the table as it stands,
 * so the standings and the projected bracket name the same field. Null for a
 * team outside the field, and for every row while the table is unranked (a
 * table nobody has played seeds nobody).
 */
export function withSeeds<
  R extends { rank: number | null; team: { id: string }; divisionId: string | null },
>(
  rows: readonly R[],
  playoffs: Pick<PlayoffSettings, 'teamCount' | 'seeding'>,
): (R & { seed: number | null })[] {
  if (rows.some((r) => r.rank === null)) return rows.map((r) => ({ ...r, seed: null }));
  const seeds = new Map(
    seedPlayoffField(
      rows.map((r) => ({ teamId: r.team.id, rank: r.rank!, divisionId: r.divisionId })),
      playoffs,
    ).map((s) => [s.teamId, s.seed]),
  );
  return rows.map((r) => ({ ...r, seed: seeds.get(r.team.id) ?? null }));
}

/** One game of the bracket, resolved as far as the results allow. */
export interface BracketPairing {
  /** `matchups.bracket_slot`. Stable for a given shape, which is what makes writing it twice a no-op. */
  slot: string;
  /** 0-based. */
  round: number;
  period: number;
  /** The higher seed. Written as the HOME team — see `advances`. */
  topSeed: number | null;
  bottomSeed: number | null;
  topTeamId: string | null;
  bottomTeamId: string | null;
  /**
   * The top seed rests and advances. No matchup is written, and `bottomTeamId`
   * is null because nobody is on the other side of it.
   */
  bye: boolean;
  /** Human-readable, for a report and for `BracketGame.label`. */
  label: string;
}

export interface BracketRoundPlan {
  round: number;
  name: string;
  /** Every period this round occupies. One, except a two-period championship. */
  periods: number[];
  games: BracketPairing[];
}

export interface BracketPlan {
  shape: BracketShape;
  rounds: BracketRoundPlan[];
  /** Everything the settings asked for that this bracket could not do. Carried, not logged. */
  problems: string[];
}

/** Which round a playoff period belongs to, given the rules. -1 when the period is not a playoff period. */
export function roundOfPeriod(playoffs: PlayoffSettings, period: number): number {
  const at = playoffs.periods.indexOf(period);
  if (at === -1) return -1;
  const rounds = bracketShape(playoffs.teamCount).rounds;
  // A two-period championship makes the last round own two entries in
  // `periods`, so an index past the last round is still the last round.
  return Math.min(at, Math.max(rounds - 1, 0));
}

/**
 * The whole bracket, round by round, resolved from the seeds and what has
 * already been decided.
 *
 * `results` maps a bracket slot to the team that came out of it. The caller
 * builds it from stored final matchups; a slot that is not in the map is a game
 * that has not been decided, and everything downstream of it comes back null.
 */
/**
 * A consolation bracket (`consolationField`), played beside the winners':
 * how deep the winners' bracket is, so its final falls in the championship's
 * first week, and whether the loser advances, the toilet bowl.
 */
export interface ConsolationPlan {
  kind: 'bracket' | 'toilet-bowl';
  winnersRounds: number;
}

/** Who goes on from a decided bracket game: its winner, or in a toilet bowl its loser. */
export function goesOn(
  game: Parameters<typeof advances>[0],
  kind: 'playoff' | 'bracket' | 'toilet-bowl' = 'playoff',
): string | null {
  const winner = advances(game);
  if (kind !== 'toilet-bowl' || winner === null) return winner;
  if (game.topTeamId === null || game.bottomTeamId === null) return winner;
  return winner === game.topTeamId ? game.bottomTeamId : game.topTeamId;
}

export function planBracket(
  field: readonly SeedRow[],
  playoffs: PlayoffSettings,
  results: ReadonlyMap<string, string> = new Map(),
  consolation: ConsolationPlan | null = null,
): BracketPlan {
  const problems: string[] = [];
  const shape = bracketShape(field.length);
  if (shape.rounds === 0) return { shape, rounds: [], problems };

  // A CONSOLATION BRACKET plays in the winners' weeks, its final in the
  // championship's first, one week a round: the rules' byes and weeks are
  // the winners' bracket's to answer to.
  const offset = consolation === null ? 0 : consolation.winnersRounds - shape.rounds;
  if (consolation === null && playoffs.byes !== shape.byes) {
    problems.push(
      `The rules ask for ${playoffs.byes} bye(s), but a field of ${field.length} in a ` +
        `${shape.size}-team bracket rests ${shape.byes}. The bracket uses ${shape.byes}.`,
    );
  }
  const legs = consolation === null ? Math.max(1, playoffs.championshipPeriods) : 1;
  const needed = shape.rounds + offset + legs - 1;
  if (consolation === null && playoffs.periods.length < needed) {
    problems.push(
      `The rules reserve ${playoffs.periods.length} playoff week(s); this bracket needs ${needed}.`,
    );
  }
  const slotOf = (round: number, game: number, leg = 0) =>
    (consolation === null ? '' : 'c-') + bracketSlot(round, shape.rounds, game, leg);

  const bySeed = new Map(field.map((f) => [f.seed, f.teamId]));
  const seedOf = new Map(field.map((f) => [f.teamId, f.seed]));
  // Slot to the team that came out of it. Seeded from what the caller has
  // stored, then extended as byes resolve themselves.
  const decided = new Map(results);

  const rounds: BracketRoundPlan[] = [];
  // Round one's sources are seeds; every later round's are the slots below it.
  type Source = { seed: number } | { slot: string };
  let sources: Source[] = bracketOrder(shape.size).map((seed) => ({ seed }));

  const resolve = (source: Source): string | null =>
    'seed' in source ? (bySeed.get(source.seed) ?? null) : (decided.get(source.slot) ?? null);

  for (let r = 0; r < shape.rounds; r += 1) {
    const last = r === shape.rounds - 1;
    const periodFor = (leg: number): number => playoffs.periods[r + offset + leg] ?? 0;

    let pairs: [Source, Source][] = [];
    for (let i = 0; i < sources.length; i += 2) pairs.push([sources[i]!, sources[i + 1]!]);

    // RESEEDING IS DECIDED WHEN THE ROUND COMES, not when the tree is drawn:
    // the highest remaining seed meets the lowest. It can only be applied once
    // every survivor is known, so a projection of a round whose feeders are
    // still being played falls back to the fixed tree — which is what the
    // bracket screen means by "Lowest remaining seed" on a TBD card.
    if (playoffs.reseed && r > 0) {
      const survivors = sources
        .map((s) => resolve(s))
        .filter((id): id is string => id !== null)
        .map((id) => ({ id, seed: seedOf.get(id) ?? Number.MAX_SAFE_INTEGER }))
        .sort((a, b) => a.seed - b.seed);
      if (survivors.length === sources.length) {
        for (const s of survivors) decided.set(`seeded:${s.id}`, s.id);
        pairs = [];
        for (let i = 0; i < survivors.length / 2; i += 1) {
          pairs.push([
            { slot: `seeded:${survivors[i]!.id}` },
            { slot: `seeded:${survivors[survivors.length - 1 - i]!.id}` },
          ]);
        }
      }
    }

    const games: BracketPairing[] = [];
    const next: Source[] = [];

    pairs.forEach(([topSource, bottomSource], g) => {
      const topId = resolve(topSource);
      const bottomId = resolve(bottomSource);
      const topSeed = topId === null ? null : (seedOf.get(topId) ?? null);
      const bottomSeed = bottomId === null ? null : (seedOf.get(bottomId) ?? null);
      // THE HIGHER SEED IS ALWAYS THE TOP OF THE CARD, and therefore the home
      // team when this is written. It is what makes "the higher seed advances"
      // a statement about a stored row rather than about a seeding table the
      // scorer would otherwise have to be handed.
      const flip = topSeed !== null && bottomSeed !== null && bottomSeed < topSeed;
      const high = flip ? bottomSeed : topSeed;
      const low = flip ? topSeed : bottomSeed;
      const highId = flip ? bottomId : topId;
      const lowId = flip ? topId : bottomId;

      // A bye: one side of this pairing is a seed the field does not reach.
      const missing =
        ('seed' in topSource && !bySeed.has(topSource.seed)) ||
        ('seed' in bottomSource && !bySeed.has(bottomSource.seed));

      for (let leg = 0; leg < (last ? legs : 1); leg += 1) {
        const slot = slotOf(r, g, leg);
        if (missing) {
          const rester = highId ?? lowId;
          const resterSeed = rester === null ? null : (seedOf.get(rester) ?? null);
          games.push({
            slot,
            round: r,
            period: periodFor(leg),
            topSeed: resterSeed,
            bottomSeed: null,
            topTeamId: rester,
            bottomTeamId: null,
            bye: true,
            label: resterSeed === null ? 'Bye' : `${resterSeed} rests`,
          });
          if (rester !== null) decided.set(slot, rester);
          continue;
        }
        games.push({
          slot,
          round: r,
          period: periodFor(leg),
          topSeed: high,
          bottomSeed: low,
          topTeamId: highId,
          bottomTeamId: lowId,
          bye: false,
          label:
            high !== null && low !== null
              ? `${high} vs ${low}`
              : `${sourceLabel(topSource, consolation?.kind)} / ${sourceLabel(bottomSource, consolation?.kind)}`,
        });
      }

      // The next round reads the FIRST leg's slot, because that is the pairing;
      // a two-period final has no next round to read anything.
      next.push({ slot: slotOf(r, g, 0) });
    });

    rounds.push({
      round: r,
      name: roundName(r, shape.rounds, consolation !== null),
      periods: Array.from({ length: last ? legs : 1 }, (_, leg) => periodFor(leg)),
      games,
    });
    sources = next;
  }

  return { shape, rounds, problems };
}

function sourceLabel(
  source: { seed: number } | { slot: string },
  kind?: 'bracket' | 'toilet-bowl',
): string {
  if ('seed' in source) return `Seed ${source.seed}`;
  if (source.slot.startsWith('seeded:')) return 'Lowest remaining seed';
  return `${kind === 'toilet-bowl' ? 'Loser' : 'Winner'} of ${source.slot}`;
}

/**
 * Who advances out of a decided game.
 *
 * A BRACKET GAME CANNOT END IN A TIE, which is the one rule a regular-season
 * matchup does not have. `decided_by` carries how it was broken; `higher-seed`
 * is the product's own answer and resolves to the home team, because
 * `planBracket` always writes the higher seed at home.
 */
export function advances(game: {
  topTeamId: string | null;
  bottomTeamId: string | null;
  topScore: number | null;
  bottomScore: number | null;
}): string | null {
  const { topTeamId, bottomTeamId, topScore, bottomScore } = game;
  if (topTeamId === null || bottomTeamId === null) return topTeamId ?? bottomTeamId;
  if (topScore === null || bottomScore === null) return null;
  if (topScore === bottomScore) return topTeamId;
  return topScore > bottomScore ? topTeamId : bottomTeamId;
}

/**
 * How a level matchup is settled, by what kind of matchup it is.
 *
 * A REGULAR-SEASON DRAW IS A DRAW UNLESS THE LEAGUE SAID OTHERWISE — the
 * default `tiebreakers.matchup` is `tie`, and inventing a winner for a league
 * that chose to allow draws is the kind of helpfulness nobody asked for.
 *
 * A BRACKET GAME IS THE EXCEPTION, AND IT IS NOT THE LEAGUE'S CHOICE: somebody
 * has to be eliminated. It goes to the higher seed, which is what ESPN and
 * Yahoo both do, and `planBracket` writes the higher seed as the home team so
 * a scorer needs no seeding table to act on it. BEHAVIORS B-102.
 *
 * Null means "leave it a draw".
 */
export function matchupTiebreak(
  kind: string,
  leagueRule: TiebreakerSettings['matchup'],
): string | null {
  if (kind === 'playoff' || kind === 'consolation') return 'higher-seed';
  return leagueRule === 'tie' ? null : leagueRule;
}
